import * as React from "react";

/**
 * Screen Reader Announcer
 * 
 * A utility component that provides live region announcements for screen readers.
 * Use this to announce dynamic content updates like:
 * - Form validation errors
 * - Toast notifications
 * - Loading states
 * - Success/error messages
 * - Navigation changes
 * 
 * @example
 * // Wrap your app with the provider
 * <SRAnnouncerProvider>
 *   <App />
 * </SRAnnouncerProvider>
 * 
 * // Use the hook to announce messages
 * const { announce } = useAnnounce();
 * announce("Form submitted successfully");
 * announce("Error: Please fill in all required fields", true); // assertive
 */

interface AnnouncerContextValue {
  /**
   * Announce a message to screen readers
   * @param message - The message to announce
   * @param assertive - If true, interrupts current speech (use for errors/urgent messages)
   */
  announce: (message: string, assertive?: boolean) => void;
  /**
   * Announce a polite message (waits for current speech to finish)
   */
  announcePolite: (message: string) => void;
  /**
   * Announce an assertive message (interrupts current speech)
   */
  announceAssertive: (message: string) => void;
}

const SRAnnouncerContext = React.createContext<AnnouncerContextValue>({
  announce: () => {},
  announcePolite: () => {},
  announceAssertive: () => {},
});

interface SRAnnouncerProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that creates live regions for screen reader announcements.
 * Should be placed near the root of your application.
 */
export const SRAnnouncerProvider: React.FC<SRAnnouncerProviderProps> = ({ children }) => {
  const [politeMessage, setPoliteMessage] = React.useState("");
  const [assertiveMessage, setAssertiveMessage] = React.useState("");

  const announce = React.useCallback((message: string, assertive = false) => {
    if (assertive) {
      // Clear first, then set after a brief delay to ensure re-announcement of same message
      setAssertiveMessage("");
      setTimeout(() => setAssertiveMessage(message), 50);
    } else {
      setPoliteMessage("");
      setTimeout(() => setPoliteMessage(message), 50);
    }
    
    // Clear messages after they've been announced (after sufficient time)
    setTimeout(() => {
      if (assertive) {
        setAssertiveMessage("");
      } else {
        setPoliteMessage("");
      }
    }, 3000);
  }, []);

  const announcePolite = React.useCallback((message: string) => {
    announce(message, false);
  }, [announce]);

  const announceAssertive = React.useCallback((message: string) => {
    announce(message, true);
  }, [announce]);

  const contextValue = React.useMemo(
    () => ({
      announce,
      announcePolite,
      announceAssertive,
    }),
    [announce, announcePolite, announceAssertive]
  );

  return (
    <SRAnnouncerContext.Provider value={contextValue}>
      {children}
      {/* 
        Live regions for screen reader announcements.
        These are visually hidden but accessible to assistive technologies.
        Using both polite and assertive regions allows for proper prioritization.
      */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {politeMessage}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {assertiveMessage}
      </div>
    </SRAnnouncerContext.Provider>
  );
};

SRAnnouncerProvider.displayName = "SRAnnouncerProvider";

/**
 * Hook to access the screen reader announcer.
 * Must be used within a SRAnnouncerProvider.
 * 
 * @example
 * const { announce } = useAnnounce();
 * 
 * // Polite announcement (default) - waits for current speech
 * announce("Your changes have been saved");
 * 
 * // Assertive announcement - interrupts current speech
 * announce("Error: Connection lost", true);
 * 
 * // Alternative methods
 * announcePolite("Loading complete");
 * announceAssertive("Session expired. Please log in again.");
 */
export const useAnnounce = (): AnnouncerContextValue => {
  const context = React.useContext(SRAnnouncerContext);
  
  if (!context) {
    // Return no-op functions if used outside provider (graceful degradation)
    console.warn("useAnnounce must be used within a SRAnnouncerProvider");
    return {
      announce: () => {},
      announcePolite: () => {},
      announceAssertive: () => {},
    };
  }
  
  return context;
};

/**
 * Standalone component for one-off announcements without the hook.
 * Useful for announcing messages in class components or when you need
 * a simple declarative approach.
 * 
 * @example
 * {error && <SRAnnouncement message={error} assertive />}
 */
interface SRAnnouncementProps {
  message: string;
  assertive?: boolean;
}

export const SRAnnouncement: React.FC<SRAnnouncementProps> = ({ message, assertive = false }) => {
  const { announce } = useAnnounce();
  const previousMessageRef = React.useRef<string>("");

  React.useEffect(() => {
    // Only announce if message has changed and is not empty
    if (message && message !== previousMessageRef.current) {
      announce(message, assertive);
      previousMessageRef.current = message;
    }
  }, [message, assertive, announce]);

  // This component doesn't render anything visible
  return null;
};

SRAnnouncement.displayName = "SRAnnouncement";

export { SRAnnouncerContext };
